import React, { useRef, useState, useEffect } from "react";
import {
  ThreeLineIcon,
  Calculator,
  Camera,
  Send,
  Close,
  stepIcon,
  audioDisable,
  audioEnable
} from "../../assets/images/index";
import { useTranslation } from 'react-i18next';
import './chat-footer.scss'
import { ChatService } from "../../services/chat/chat.services";
import { UserService } from "../../services/user/user.service";
import { Loader } from "../../assets/images/index";
import { useNavigate } from "react-router-dom";
import { RouterConstant } from "../../routers/index";
import MathExpressionNew from '../../components/common/mathjax'; // Adjust the import path as needed
import { isMobileDevice } from "../../utils/utils"; // Adjust the import path as needed
import { ExerciseQuestionsService } from "../../services/exercise-questions/exercise-questions.service";
import ExerciseQuestionList from "../../components/exercise-question-list";
import QuestionStepsList from "../../components/question-steps-list";
import { useQuestionsStore } from "../../utils/questionStore"; // Import the zustand store
import { useUserStore } from "../../utils/userStore";
import { useStepsStore } from "../../utils/stepStore";
import useAudioStore from "../../utils/audioStore";
import { useExerciseStore } from "../../utils/exerciseStore";
import { hebrewLetters } from '../../configs/config';
import { CaptureService } from "../../services/capture-exercise/capture-exercise.service";

type PopupType = 'calculator' | 'exerciseQuestions' | 'steps' | 'audio' | null;

export interface Step {
  title: string;
  content: string;
}

interface ChatFooterProps {
  inputMessage: string;
  setInputMessage: React.Dispatch<React.SetStateAction<string>>;
  handleSubmit: (e: React.FormEvent) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  selectedQuestionId?: number;
  selectedQuestion?: string;
  currentStepIndex?: number;
  titleText: string;
  placeholderText: string;
  shouldShowButtons?: boolean;
  isSendDisabled?: boolean;
  clearInputOnSend?: boolean;
  disabled?: boolean;
}

export default function ChatFooter({
  inputMessage,
  setInputMessage,
  handleSubmit,
  handleKeyDown,
  isLoading,
  selectedQuestionId,
  selectedQuestion,
  currentStepIndex,
  titleText,
  placeholderText,
  shouldShowButtons = true,
  isSendDisabled = false,
  clearInputOnSend = true,
  disabled = false
}: ChatFooterProps) {
  const { t } = useTranslation();  
  const navigate = useNavigate();
  const captureService = new CaptureService();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { ROUTERS_PATH } = RouterConstant;
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [loadingExerciseContent, setLoadingExerciseContent] = useState(false);
  const [popupType, setPopupType] = useState<PopupType>(null); // Track popup type
  const [exerciseContent, setExerciseContent] = useState<string | null>(null);
  const [exerciseQuestions, setExerciseQuestions] = useState<{ id: number; question: string }[]>([]);
  const selectedExerciseId = useExerciseStore((s) => s.selectedExerciseId);
  const chatService = new ChatService();
  const userService = new UserService();
  const [sessionId, setSessionId] = useState("");
  const [selectedStep, setSelectedStep] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [exerciseImage, setExerciseImage] = useState<string | null>(null);
  const steps = useQuestionsStore.getState().getStepsByQuestionId?.(selectedQuestionId ?? 0) || [];
  const audioStore = useAudioStore();
  const isAudioEnabled = useUserStore((state) => state.user_attributes.isEnableAudio);
  const updateAttribute = useUserStore((state) => state.updateAttribute);
  const currentSessionId = useExerciseStore((state) => state.captureImageSessionId);
  const stepHistory = useStepsStore((s) => 
    s.getStepHistory(selectedQuestionId ?? 0)
  );

  useEffect(() => {
    if (currentSessionId) {
      fetchValidatedExercise(currentSessionId);
    }
  }, [currentSessionId]);

  const fetchValidatedExercise = async (sessionId: string) => {
    setLoadingExerciseContent(true)
    try {
      const response = await captureService.GETVALIDATEEXECISE(sessionId);
      if (response.status === "success") {
        if (response.capture_image_b64) {
          setExerciseImage(response.capture_image_b64);
        }
      }
    } catch (error) {
      console.error("Error fetching validated exercise:", error);
    } finally {
      setLoadingExerciseContent(false);
    }
  };

  const fetchExerciseContent = async (exerciseId: number) => {
    setLoadingExerciseContent(true);
    setExerciseContent(null);
    setIsPopupOpen(true);
    setPopupType('calculator');
    try {
      if (exerciseId){
        const response = await chatService.GETEXERCISECONTENT(exerciseId);
        setExerciseContent(response.exercise_content);
      }
    } catch (error) {
      console.error("Failed to fetch exercise content:", error);
    } finally {
      setLoadingExerciseContent(false);
    }
  };

  const handleAccordionChange = (panel: string, index: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
      setSelectedStep(isExpanded ? index : null);
  };

  const handleDisplayExerciseQuestions = async () => {
    setExerciseQuestions([]);
    setIsPopupOpen(true);
    setPopupType('exerciseQuestions');
    const exerciseServiceInstance: ExerciseQuestionsService = new ExerciseQuestionsService();
    const response = await exerciseServiceInstance.fetchExerciseQuestions(selectedExerciseId);
    setExerciseQuestions(response)
    const history = useQuestionsStore.getState().getQuestionHistory(selectedExerciseId);
    let completed: number[] = [];
    let in_process: number[] = [];
    if (history && history.exercise_id === selectedExerciseId) {
      completed = history.completed || [];
      in_process = history.in_process || [];
    }
    setExerciseQuestions(
      response.map((q: any) => ({
        ...q,
        isCompleted: completed.includes(q.id),
        isInProcess: in_process.includes(q.id)
      }))
    );
  }

  const handleDisplaySteps = async () => {
    setPopupType("steps");
    setIsPopupOpen(true);
    const defaultPanelId = `panel${currentStepIndex}`;
    setExpanded(defaultPanelId);
    setSelectedStep(currentStepIndex ?? null);
  };

  const handleToggleAudio = async () => {
    const newMuteState = !isAudioEnabled; // isAudioEnabled is actually isMuteActive (True = Muted)
    updateAttribute("isEnableAudio", newMuteState);
    if (newMuteState) {
      audioStore.stopAudio();
    }
  
    try {
      await userService.updateUserAttribute("isEnableAudio", newAudioState);
    } catch (error) {
      console.error("Error updating audio setting:", error);
    }
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    setExerciseContent(null);
    setExerciseQuestions([]);
    // setSteps([]);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputMessage(e.target.value);

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
    }
  };

  const handleKeyDownModified = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      // Let the default behavior (new line) happen
      // We don't call handleSendMessage here anymore as per user request
    }
  };

  const handleSendMessage = (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!inputMessage.trim()) return;

    handleSubmit(e as any);

    if (clearInputOnSend) {
      setInputMessage("");
    }

    if (textareaRef.current) {
      textareaRef.current.style.height = "40px";
    }
  };

  const handleCaptureExercise = () => {
    const sessionId = Date.now().toString(36) + Math.random().toString(36).substr(2);
    navigate(`${ROUTERS_PATH.CAPTUREEXERCISE}?sessionId=${sessionId}`);
  };

  const showExerciseQuestionsList = () => {
    navigate(ROUTERS_PATH.EXERCISEQUESTIONS);
  };

  const getPopupConfig = (type: PopupType) => {
    switch (type) {
      case 'calculator':
        return { icon: Calculator, title: t("exercise") };
      case 'exerciseQuestions':
        return { icon: ThreeLineIcon, title: t("exerciseQuestions") };
      case 'steps':
        return { icon: stepIcon, title: t("sectionSteps") };
      case 'audio':
        return { icon: audioDisable, title: t("audio") };
      default:
        return { icon: audioDisable, title: t("audio") };
    }
  };
 

  return (
    <>
      <div className="chat-footer">
        {shouldShowButtons && (
          <div className="chat-footer-btns">
            <div className="chat-footer-btns-group">
              <button onClick={() => fetchExerciseContent(selectedExerciseId)}>
                <img src={Calculator} alt="action" />
              </button>
              <button 
                onClick={() => handleDisplayExerciseQuestions()} 
                disabled={!selectedExerciseId} 
                className={!selectedExerciseId ? "disabled" : ""}
              >
                <img src={ThreeLineIcon} alt="ThreeLineIcon" />
              </button>
              <button 
                onClick={() => handleDisplaySteps()}
                disabled={steps.length === 0} 
                className={steps.length === 0 ? "disabled" : ""}
              >
                <img src={stepIcon} alt="Steps" />
              </button>
              <button onClick={handleToggleAudio}>
                {isAudioEnabled ? (
                  <img src={audioDisable} alt="Audio Disabled"/>
                ) : (
                  <img src={audioEnable} alt="Audio Enabled"/>
                )}
              </button>
            </div>
            <button onClick={() => handleCaptureExercise()}>
              <img src={Camera} alt="Camera" />
            </button>
          </div>)}
        {/* Popup */}

        {/* Existing form */}
        <form onSubmit={handleSendMessage} className="chat-footer-form">
          <div className="form">
            <p>{titleText ? titleText : ""}</p>
            <textarea
              ref={textareaRef}
              className="message-input"
              value={inputMessage}
              placeholder={placeholderText ? placeholderText : t("anyQuestions")}
              onChange={handleInputChange}
              onKeyDown={handleKeyDownModified}
              rows={1}
              disabled={disabled}
            />
          </div>
          <button
            type="submit"
            disabled={isLoading || isSendDisabled}
            className={`chat-footer-form-btn ${isLoading || isSendDisabled ? "disabled" : ""}`}
          >
            <img src={Send} alt="Send" />
          </button>
        </form>
      </div>
      {isPopupOpen && (
        <div className="popup-overlay">
          <div className="popup-container slide-up">
            <div className="popup-header">
              <div className="popup-header-inner">
                <img src={getPopupConfig(popupType).icon} alt="action" />
                <div>
                  <h1 className="popup-header-title">
                    {getPopupConfig(popupType).title}
                  </h1>
                </div>
              </div>
              <button className="popup-close-btn" onClick={closePopup}>
                <img src={Close} alt="Close" />
              </button>
            </div>

            <div className="popup-body">
              {popupType === 'calculator' ? (
                <div className="exercise-content">
                  {loadingExerciseContent ? (
                    <div className="loaderBlock">
                      <img src={Loader} alt="Loading" />
                    </div>
                  ) : exerciseContent ? (
                    <MathExpressionNew content={exerciseContent} />
                  ) : exerciseImage ? (
                    <img src={exerciseImage} alt="Exercise" className="exercise-image" />
                  ) : (
                    <div className="loaderBlock">
                      <img src={Loader} alt="Loading" />
                    </div>
                  )}
                </div>
              ) : popupType === 'exerciseQuestions' && exerciseQuestions.length > 0 ? (
                <div>
                  <ExerciseQuestionList
                    exerciseQuestions={exerciseQuestions}
                    hebrewLetters={hebrewLetters}
                    isViewOnly={true}
                    selectedQuestionId={selectedQuestionId}
                  />
                </div>
              ) : popupType === 'steps' && steps.length > 0 ? (
                <div className="full-template question-steps">
                  <QuestionStepsList
                    steps={steps}
                    expanded={expanded}
                    selectedStep={selectedStep}
                    onAccordionChange={handleAccordionChange}
                    isViewOnly={true}
                    currentStepIndex={currentStepIndex}
                    stepHistory={stepHistory}
                  />
                </div>
              ) : (
                <div className="loaderBlock">
                  <img src={Loader} alt="Loading" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}